import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';
import { useBackgroundJobs } from './BackgroundJobsContext';

interface EnrichmentJob {
  id: number;
  file_name: string;
  enrichment_type: string;
  total_rows: number;
  processed_rows: number;
  successful_rows: number;
  failed_rows: number;
  credits_used: number;
  status: string;
  uploaded_at: string;
  completed_at: string | null;
  results_count: number;
}

// Expected fields for enrichment
const EXPECTED_FIELDS = {
  candidate: [
    { key: 'linkedin_url', label: 'LinkedIn Profile URL', required: false },
    { key: 'first_name', label: 'First Name', required: false },
    { key: 'last_name', label: 'Last Name', required: false },
    { key: 'company', label: 'Company Name', required: false }
  ],
  company: [
    { key: 'linkedin_url', label: 'LinkedIn Company URL', required: false },
    { key: 'domain', label: 'Company Domain', required: false }
  ]
};

const DataEnrichment: React.FC = () => {
  const { user } = useAuth();
  const { startOperation } = useBackgroundJobs();
  const [jobs, setJobs] = useState<EnrichmentJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState<number | null>(null);
  const [projects, setProjects] = useState<any[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);
  const [addToProjectTag, setAddToProjectTag] = useState<string>('');

  // Add-to-project progress tracking
  const [addToProjectOp, setAddToProjectOp] = useState<{
    operationId: string;
    totalRecords: number;
    processedRecords: number;
    addedCount: number;
    updatedCount: number;
    skippedCount: number;
    status: string;
  } | null>(null);
  const addToProjectPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  
  // Column mapping state
  const [showMapping, setShowMapping] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [enrichmentType, setEnrichmentType] = useState<'candidate' | 'company'>('candidate');
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [columnMapping, setColumnMapping] = useState<{[key: string]: string}>({});
  
  const candidateFileInputRef = useRef<HTMLInputElement>(null);
  const companyFileInputRef = useRef<HTMLInputElement>(null);
  const resumeFileInputRef = useRef<HTMLInputElement>(null);
  const [resumeJobId, setResumeJobId] = useState<number | null>(null);
  const [resumeJobType, setResumeJobType] = useState<'candidate' | 'company'>('candidate');

  // Failures modal state
  const [showFailuresModal, setShowFailuresModal] = useState(false);
  const [failuresJobId, setFailuresJobId] = useState<number | null>(null);
  const [failures, setFailures] = useState<{ id: number; linkedin_url: string; error_message: string; created_at: string }[]>([]);
  const [loadingFailures, setLoadingFailures] = useState(false);

  // Retry state
  const [retryOp, setRetryOp] = useState<{
    operationId: string;
    totalRecords: number;
    processedRecords: number;
    successCount: number;
    failedCount: number;
    status: string;
  } | null>(null);
  const retryPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Fetch enrichment jobs
  const fetchJobs = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/enrichment/jobs', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setJobs(data.jobs || []);
      } else {
        console.error('Failed to fetch jobs');
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch projects (admin sees all users' projects)
  const fetchProjects = async () => {
    try {
      const token = localStorage.getItem('token');
      const isAdmin = user?.email === 'alex@marketmapr.com' || localStorage.getItem('adminEmail') === 'alex@marketmapr.com';
      const url = isAdmin ? '/api/projects?all=true' : '/api/projects';
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setProjects(data);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  // Keep a ref to the latest jobs so the polling interval always sees current state
  const jobsRef = useRef(jobs);
  useEffect(() => { jobsRef.current = jobs; }, [jobs]);

  useEffect(() => {
    fetchJobs();
    fetchProjects();

    // Poll for updates every 5 seconds if there are processing jobs
    const interval = setInterval(() => {
      const hasProcessingJobs = jobsRef.current.some(job => job.status === 'processing');
      if (hasProcessingJobs) {
        fetchJobs();
      }
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  // Handle file selection (parse CSV headers)
  const handleFileSelect = (type: 'candidate' | 'company', file: File) => {
    setSelectedFile(file);
    setEnrichmentType(type);
    setError(null);

    const reader = new FileReader();
    reader.onload = (e) => {
      const rawText = e.target?.result as string;
      // Strip BOM (Excel UTF-8 CSVs often start with \uFEFF)
      const text = rawText.replace(/^\uFEFF/, '');
      const firstLine = text.split('\n')[0].replace(/\r$/, '');
      const headers = firstLine.split(',').map(h => h.trim().replace(/"/g, ''));
      setCsvHeaders(headers);
      
      // Auto-map columns
      const autoMapping: {[key: string]: string} = {};
      const expectedFields = EXPECTED_FIELDS[type];
      
      expectedFields.forEach(field => {
        const normalizedFieldKey = field.key.toLowerCase().replace(/_/g, '');
        
        const matchingHeader = headers.find(header => {
          const normalizedHeader = header.toLowerCase().replace(/[_\s]/g, '');
          
          // Direct match
          if (normalizedHeader === normalizedFieldKey) return true;
          
          // Partial match
          if (normalizedHeader.includes(normalizedFieldKey) || normalizedFieldKey.includes(normalizedHeader)) return true;
          
          // Special cases
          if (field.key === 'linkedin_url' && normalizedHeader.includes('linkedin') && normalizedHeader.includes('url')) return true;
          if (field.key === 'linkedin_url' && normalizedHeader.includes('liprofile')) return true;
          if (field.key === 'first_name' && (normalizedHeader === 'firstname' || normalizedHeader === 'fname')) return true;
          if (field.key === 'last_name' && (normalizedHeader === 'lastname' || normalizedHeader === 'lname' || normalizedHeader === 'surname')) return true;
          if (field.key === 'company' && (normalizedHeader === 'companyname' || normalizedHeader === 'organization')) return true;
          if (field.key === 'domain' && (normalizedHeader === 'companydomain' || normalizedHeader === 'website')) return true;
          
          return false;
        });
        
        if (matchingHeader) {
          autoMapping[field.key] = matchingHeader;
        }
      });
      
      setColumnMapping(autoMapping);
      setShowMapping(true);
    };
    reader.readAsText(file);
  };

  // Proceed with upload after mapping
  const handleUploadWithMapping = async () => {
    if (!selectedFile) return;

    // Validate that user has mapped at least one valid combination
    const hasLinkedInUrl = columnMapping['linkedin_url'];
    const hasNameAndCompany = enrichmentType === 'candidate' && 
      columnMapping['first_name'] && 
      columnMapping['last_name'] && 
      columnMapping['company'];
    const hasDomain = enrichmentType === 'company' && columnMapping['domain'];

    if (!hasLinkedInUrl && !hasNameAndCompany && !hasDomain) {
      setError(
        enrichmentType === 'candidate'
          ? 'Please map either: LinkedIn URL OR (First Name + Last Name + Company)'
          : 'Please map either: LinkedIn URL OR Domain'
      );
      return;
    }

    setUploading(true);
    setError(null);
    setShowMapping(false);

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('type', enrichmentType);
    formData.append('columnMapping', JSON.stringify(columnMapping));

    try {
      const token = localStorage.getItem('token');
      const endpoint = resumeJobId
        ? `/api/enrichment/resume/${resumeJobId}`
        : '/api/enrichment/upload';

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        if (resumeJobId) {
          alert(`Resuming enrichment! ${data.alreadyProcessed} already enriched rows will be skipped.`);
        } else {
          alert(`Enrichment started! Processing ${selectedFile.name}...`);
        }

        // Refresh credit balance
        if ((window as any).refreshCreditBalance) {
          (window as any).refreshCreditBalance();
        }

        // Refresh jobs list
        fetchJobs();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      setError('Failed to upload file');
    } finally {
      setUploading(false);
      setSelectedFile(null);
      setResumeJobId(null);

      // Reset file inputs
      if (candidateFileInputRef.current) candidateFileInputRef.current.value = '';
      if (companyFileInputRef.current) companyFileInputRef.current.value = '';
      if (resumeFileInputRef.current) resumeFileInputRef.current.value = '';
    }
  };

  // Clean up polling on unmount
  useEffect(() => {
    return () => {
      if (addToProjectPollRef.current) clearInterval(addToProjectPollRef.current);
      if (retryPollRef.current) clearInterval(retryPollRef.current);
    };
  }, []);

  // Handle add to project - starts background operation and closes modal immediately
  const handleAddToProject = async () => {
    if (!selectedJobId || !selectedProjectId) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/enrichment/add-to-project', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          jobId: selectedJobId,
          projectId: selectedProjectId,
          tags: addToProjectTag.trim() ? addToProjectTag.split(',').map(t => t.trim()).filter(t => t.length > 0) : []
        })
      });

      if (response.ok) {
        const data = await response.json();

        if (!data.operationId) {
          alert(data.message || 'No candidates to add.');
          setShowProjectModal(false);
          return;
        }

        // Queue to background jobs context - modal closes immediately
        startOperation({
          operationId: data.operationId,
          jobId: selectedJobId,
          projectId: selectedProjectId,
          jobName: data.jobName || `Job #${selectedJobId}`,
          projectName: data.projectName || `Project #${selectedProjectId}`,
          totalRecords: data.totalRecords
        });

        // Close modal and reset - user can move on
        setShowProjectModal(false);
        setAddToProjectOp(null);
        setSelectedProjectId(null);
        setAddToProjectTag('');
        fetchJobs();
      } else {
        const errorData = await response.json();
        alert(`Error: ${errorData.error}`);
      }
    } catch (error) {
      console.error('Error adding to project:', error);
      alert('Failed to add to project');
    }
  };

  // Close modal (for cancel or legacy)
  const handleCloseAddToProject = () => {
    setShowProjectModal(false);
    setAddToProjectOp(null);
    setSelectedProjectId(null);
    setAddToProjectTag('');
    if (addToProjectPollRef.current) {
      clearInterval(addToProjectPollRef.current);
      addToProjectPollRef.current = null;
    }
    fetchJobs();
  };

  // Fetch failures for a job
  const handleViewFailures = async (jobId: number) => {
    setFailuresJobId(jobId);
    setShowFailuresModal(true);
    setLoadingFailures(true);
    setRetryOp(null);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/enrichment/jobs/${jobId}/failures`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setFailures(data.failures || []);
      }
    } catch (error) {
      console.error('Error fetching failures:', error);
    } finally {
      setLoadingFailures(false);
    }
  };

  // Check if an error is retryable (429 rate limit / concurrency)
  const isRetryable = (errorMessage: string) => {
    const msg = errorMessage.toLowerCase();
    return msg.includes('429') || msg.includes('rate limit') || msg.includes('concurrency');
  };

  // Retry retryable failures
  const handleRetryFailed = async () => {
    if (!failuresJobId) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/enrichment/jobs/${failuresJobId}/retry-failed`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });

      if (response.ok) {
        const data = await response.json();
        if (!data.operationId) {
          alert(data.message || 'No retryable failures found.');
          return;
        }

        setRetryOp({
          operationId: data.operationId,
          totalRecords: data.retryableCount,
          processedRecords: 0,
          successCount: 0,
          failedCount: 0,
          status: 'processing'
        });

        const pollInterval = setInterval(async () => {
          try {
            const progressRes = await fetch(`/api/enrichment/jobs/${failuresJobId}/retry-progress/${data.operationId}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (progressRes.ok) {
              const progress = await progressRes.json();
              setRetryOp(prev => prev ? {
                ...prev,
                processedRecords: progress.processedRecords || 0,
                successCount: progress.successCount || 0,
                failedCount: progress.failedCount || 0,
                status: progress.status
              } : null);

              if (progress.status === 'completed' || progress.status === 'error') {
                clearInterval(pollInterval);
                retryPollRef.current = null;
              }
            }
          } catch (e) { /* ignore polling errors */ }
        }, 2000);
        retryPollRef.current = pollInterval;
      } else {
        const errorData = await response.json();
        alert(`Error: ${errorData.error}`);
      }
    } catch (error) {
      console.error('Error retrying failed records:', error);
      alert('Failed to retry');
    }
  };

  // Close failures modal
  const handleCloseFailures = () => {
    setShowFailuresModal(false);
    setFailuresJobId(null);
    setFailures([]);
    setRetryOp(null);
    if (retryPollRef.current) {
      clearInterval(retryPollRef.current);
      retryPollRef.current = null;
    }
    fetchJobs();
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <div className="space-y-6">
      {/* Upload Section */}
      <div className="bg-blue-50 border border-blue-200/80 rounded-xl p-8 shadow-card">
        <h2 className="text-2xl font-bold text-gray-900 mb-2 tracking-tight">Enrich Your Data</h2>
        <p className="text-gray-600 mb-6">
          Upload a CSV of LinkedIn profiles or company domains to get comprehensive enrichment
        </p>

        <div className="flex space-x-4">
          {/* Upload Candidates CSV */}
          <div className="flex-1">
            <input
              ref={candidateFileInputRef}
              type="file"
              accept=".csv"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleFileSelect('candidate', file);
              }}
            />
            <button
              onClick={() => candidateFileInputRef.current?.click()}
              disabled={uploading}
              className="btn-primary w-full px-6 py-4 text-base font-semibold"
            >
              {uploading ? '⏳ Uploading...' : '📥 Upload Candidates CSV'}
            </button>
          </div>

          {/* Upload Companies CSV */}
          <div className="flex-1">
            <input
              ref={companyFileInputRef}
              type="file"
              accept=".csv"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleFileSelect('company', file);
              }}
            />
            <button
              onClick={() => companyFileInputRef.current?.click()}
              disabled={uploading}
              className="btn-secondary w-full px-6 py-4 text-base font-semibold border-2"
            >
              {uploading ? '⏳ Uploading...' : '🏢 Upload Companies CSV'}
            </button>
          </div>
        </div>

        {/* Hidden file input for resume uploads */}
        <input
          ref={resumeFileInputRef}
          type="file"
          accept=".csv"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleFileSelect(resumeJobType, file);
          }}
        />

        <div className="mt-4 text-sm text-gray-600">
          <p>
            <span className="font-semibold">Cost:</span> 1 credit per profile enrichment |{' '}
            <span className="font-semibold">Required:</span> LinkedIn URL or Name + Company (for candidates) / LinkedIn URL or Domain (for companies)
          </p>
        </div>

        {error && (
          <div className="mt-4 bg-red-50 border border-red-200 rounded-xl p-3 shadow-card animate-slide-in-up">
            <p className="text-red-800">{error}</p>
          </div>
        )}
      </div>

      {/* Column Mapping Modal */}
      {showMapping && selectedFile && (
        <div className="bg-blue-50 border border-blue-200/80 rounded-xl p-6 mb-4 shadow-card animate-slide-in-up">
          <h3 className="text-lg font-medium text-blue-900 mb-3 tracking-tight">
            {resumeJobId ? 'Resume Enrichment - Map Your CSV Columns' : 'Map Your CSV Columns'}
          </h3>
          <p className="text-sm text-blue-700 mb-4">
            {resumeJobId
              ? 'Re-upload the same CSV. Already enriched rows will be automatically skipped.'
              : 'Map your CSV columns to the required fields. You need either a LinkedIn URL OR the alternative fields.'}
          </p>
          
          <div className="space-y-3 max-h-80 overflow-y-auto bg-white p-4 rounded-xl border border-gray-200/80">
            {EXPECTED_FIELDS[enrichmentType].map((field) => (
              <div key={field.key} className="flex items-center space-x-3">
                <label className="w-1/3 text-sm font-medium text-gray-700">
                  {field.label}
                </label>
                <select
                  value={columnMapping[field.key] || ''}
                  onChange={(e) => setColumnMapping({
                    ...columnMapping,
                    [field.key]: e.target.value
                  })}
                  className="input w-2/3"
                >
                  <option value="">-- Skip this field --</option>
                  {csvHeaders.map((header, index) => (
                    <option key={index} value={header}>{header}</option>
                  ))}
                </select>
              </div>
            ))}
          </div>
          
          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200/80 rounded-xl text-sm text-yellow-800">
            <strong>Required mapping:</strong>
            {enrichmentType === 'candidate' ? (
              <> Either map <strong>LinkedIn Profile URL</strong> OR map <strong>First Name + Last Name + Company Name</strong></>
            ) : (
              <> Either map <strong>LinkedIn Company URL</strong> OR map <strong>Company Domain</strong></>
            )}
          </div>
          
          <div className="mt-4 flex space-x-2">
            <button
              onClick={handleUploadWithMapping}
              className="btn-primary"
            >
              {resumeJobId ? 'Resume Enrichment' : 'Continue with Upload'}
            </button>
            <button
              onClick={() => {
                setShowMapping(false);
                setSelectedFile(null);
                setCsvHeaders([]);
                setColumnMapping({});
                setResumeJobId(null);
                if (candidateFileInputRef.current) candidateFileInputRef.current.value = '';
                if (companyFileInputRef.current) companyFileInputRef.current.value = '';
                if (resumeFileInputRef.current) resumeFileInputRef.current.value = '';
              }}
              className="btn-secondary"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Enrichment History */}
      <div className="card">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900 tracking-tight">Enrichment History</h2>
        </div>

        {loading ? (
          <div className="px-6 py-12 text-center text-gray-500">
            Loading enrichment history...
          </div>
        ) : jobs.length === 0 ? (
          <div className="px-6 py-12 text-center text-gray-500">
            No enrichment jobs yet. Upload a CSV to get started!
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {jobs.map((job) => (
              <div key={job.id} className="px-6 py-4 hover:bg-blue-50/40 transition-colors duration-150">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="font-semibold text-gray-900">{job.file_name}</h3>
                      <span
                        className={`badge ${
                          job.status === 'completed'
                            ? 'badge-green'
                            : job.status === 'processing'
                            ? 'badge-blue'
                            : 'badge-red'
                        }`}
                      >
                        {job.status}
                      </span>
                    </div>

                    <div className="flex space-x-6 text-sm text-gray-600 mb-2">
                      <span>
                        Type: <span className="font-medium capitalize">{job.enrichment_type}s</span>
                      </span>
                      <span>
                        Rows:{' '}
                        <span className="font-medium">
                          {job.processed_rows}/{job.total_rows}
                        </span>
                      </span>
                      <span>
                        Credits Used: <span className="font-medium">{job.credits_used}</span>
                      </span>
                      <span>
                        Date: <span className="font-medium">{formatDate(job.uploaded_at)}</span>
                      </span>
                    </div>

                    {/* Progress Bar for Processing Jobs */}
                    {job.status === 'processing' && job.total_rows > 0 && (
                      <div className="w-full bg-gray-200 rounded-full h-2 mt-3">
                        <div
                          className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                          style={{
                            width: `${(job.processed_rows / job.total_rows) * 100}%`
                          }}
                        ></div>
                      </div>
                    )}

                    {/* Success/Failure counts for completed jobs */}
                    {job.status === 'completed' && (
                      <div className="flex space-x-4 text-sm mt-2">
                        <span className="text-green-600">
                          ✓ {job.successful_rows} successful
                        </span>
                        {job.failed_rows > 0 && (
                          <button
                            onClick={() => handleViewFailures(job.id)}
                            className="text-red-600 hover:text-red-800 hover:underline cursor-pointer"
                          >
                            ✗ {job.failed_rows} failed
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col gap-2 ml-4">
                    {/* Resume Button - shown when job stopped before finishing */}
                    {job.total_rows > 0 && job.processed_rows < job.total_rows && (
                      <button
                        onClick={() => {
                          setResumeJobId(job.id);
                          setResumeJobType(job.enrichment_type as 'candidate' | 'company');
                          setEnrichmentType(job.enrichment_type as 'candidate' | 'company');
                          resumeFileInputRef.current?.click();
                        }}
                        className="px-4 py-2 text-sm text-orange-600 hover:text-orange-800 font-medium border border-orange-600 rounded-lg hover:bg-orange-50 transition-all duration-200 hover:-translate-y-px"
                      >
                        Resume ({job.total_rows - job.processed_rows} remaining)
                      </button>
                    )}

                    {/* Add to Project Button */}
                    {job.successful_rows > 0 && (
                      <button
                        onClick={() => {
                          setSelectedJobId(job.id);
                          setShowProjectModal(true);
                        }}
                        className="px-4 py-2 text-sm text-blue-600 hover:text-blue-800 font-medium border border-blue-600 rounded-lg hover:bg-blue-50 transition-all duration-200 hover:-translate-y-px"
                      >
                        Add to Project →
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Project Selection Modal */}
      {showProjectModal && (
        <div className="modal-backdrop flex items-center justify-center">
          <div className="modal-dialog p-6 max-w-md w-full">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 tracking-tight">
              Select Project
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Choose which project to add the enriched candidates to:
            </p>

            <select
              value={selectedProjectId || ''}
              onChange={(e) => setSelectedProjectId(Number(e.target.value))}
              className="input mb-4"
            >
              <option value="">-- Select a project --</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.owner_email ? `[${project.owner_email}] ${project.name}` : project.name}
                </option>
              ))}
            </select>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tag candidates <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <input
                type="text"
                value={addToProjectTag}
                onChange={(e) => setAddToProjectTag(e.target.value)}
                placeholder="e.g. Upload 1, Shortlist A"
                className="input"
              />
              <p className="text-xs text-gray-400 mt-1">
                Separate multiple tags with commas. Job will run in the background — check the notification bell for progress.
              </p>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={handleAddToProject}
                disabled={!selectedProjectId}
                className="btn-primary flex-1"
              >
                Add to Project
              </button>
              <button
                onClick={() => {
                  setShowProjectModal(false);
                  setSelectedProjectId(null);
                  setAddToProjectTag('');
                }}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Failures Modal */}
      {showFailuresModal && (
        <div className="modal-backdrop flex items-center justify-center">
          <div className="modal-dialog p-6 max-w-2xl w-full max-h-[80vh] flex flex-col">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-gray-900 tracking-tight">
                Failed Enrichments
              </h3>
              <button
                onClick={handleCloseFailures}
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none transition-colors duration-200"
              >
                &times;
              </button>
            </div>

            {loadingFailures ? (
              <p className="text-sm text-gray-500">Loading failures...</p>
            ) : (
              <>
                {/* Summary */}
                {(() => {
                  const retryableCount = failures.filter(f => isRetryable(f.error_message)).length;
                  const permanentCount = failures.length - retryableCount;
                  return (
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex space-x-4 text-sm">
                        <span className="text-gray-600">{failures.length} total failures</span>
                        {retryableCount > 0 && (
                          <span className="text-orange-600 font-medium">{retryableCount} retryable (rate limit)</span>
                        )}
                        {permanentCount > 0 && (
                          <span className="text-red-600">{permanentCount} permanent</span>
                        )}
                      </div>
                      {retryableCount > 0 && !retryOp && (
                        <button
                          onClick={handleRetryFailed}
                          className="px-4 py-2 text-sm bg-orange-500 text-white rounded-lg hover:bg-orange-600 font-medium transition-all duration-200 hover:-translate-y-px hover:shadow-md"
                        >
                          Retry {retryableCount} Failed
                        </button>
                      )}
                    </div>
                  );
                })()}

                {/* Retry progress */}
                {retryOp && (
                  <div className="mb-4 p-3 bg-gray-50 rounded-xl border border-gray-200/80">
                    <p className="text-sm font-medium text-gray-700 mb-2">
                      {retryOp.status === 'completed' ? 'Retry Complete' : retryOp.status === 'error' ? 'Retry Error' : 'Retrying...'}
                    </p>
                    {retryOp.status === 'processing' && (
                      <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                        <div
                          className="bg-orange-500 h-2 rounded-full transition-all duration-500"
                          style={{ width: `${retryOp.totalRecords > 0 ? Math.round((retryOp.processedRecords / retryOp.totalRecords) * 100) : 0}%` }}
                        ></div>
                      </div>
                    )}
                    <div className="flex space-x-4 text-sm">
                      <span className="text-gray-600">{retryOp.processedRecords} / {retryOp.totalRecords} processed</span>
                      {retryOp.successCount > 0 && <span className="text-green-600">{retryOp.successCount} succeeded</span>}
                      {retryOp.failedCount > 0 && <span className="text-red-600">{retryOp.failedCount} still failed</span>}
                    </div>
                    {retryOp.status === 'completed' && (
                      <button
                        onClick={() => handleViewFailures(failuresJobId!)}
                        className="mt-2 text-sm text-blue-600 hover:underline"
                      >
                        Refresh list
                      </button>
                    )}
                  </div>
                )}

                {/* Failures list */}
                <div className="overflow-y-auto flex-1 border border-gray-200/80 rounded-xl">
                  <table className="w-full text-sm">
                    <thead className="table-header sticky top-0">
                      <tr>
                        <th className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">LinkedIn URL</th>
                        <th className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">Error</th>
                        <th className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wider w-20">Type</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {failures.map((f) => (
                        <tr key={f.id} className="table-row">
                          <td className="px-3 py-2 text-gray-700 truncate max-w-[250px]" title={f.linkedin_url}>
                            {f.linkedin_url || '—'}
                          </td>
                          <td className="px-3 py-2 text-gray-600 truncate max-w-[300px]" title={f.error_message}>
                            {f.error_message || '—'}
                          </td>
                          <td className="px-3 py-2">
                            {isRetryable(f.error_message) ? (
                              <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-orange-100 text-orange-700">
                                429
                              </span>
                            ) : (
                              <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-700">
                                Perm
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                      {failures.length === 0 && (
                        <tr>
                          <td colSpan={3} className="px-3 py-4 text-center text-gray-400">No failures found</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DataEnrichment;
